import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Badge,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useSamarthDashboardContext } from "utils/samarth-dashboard-context/hooks/useSamarthDashboardContext";
import { ChartDialog } from "./chart-dialog/chart-dialog";
import style from "./external-disruption.module.css";

export default function ExternalDisruption() {
  const { state } = useSamarthDashboardContext();

  const disruptions = state.dashboardData.externalDisruptions;

  const headers = [
    "Risk Type",
    "Incident Headline",
    "City",
    "State",
    "Country",
    "Affected Facility Count",
    "Severity",
    "Incident Score",
    "Start Date",
    "End Date",
    "Created Date",
    "Lane"
  ];

  const DisruptionTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: "transparent",
        tableLayout: "auto",
        width: "100%",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  backgroundColor: "#0c1c3d",
                  color: "#ccc",
                  fontSize: 16,
                  whiteSpace: "nowrap",
                  px: 2,
                  py: 2, 
                  fontWeight: "bold",
                }}
                style={{
                  borderColor: "#162456",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "fit-content",
                  }}
                >
                  {header} <UnfoldMoreOutlinedIcon fontSize="small" />
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {disruptions.map((disruption, index) => (
            <TableRow key={index} hover>
              <TableCell
                className={style.tableCell}
              >
                <div className={style.riskType}
                >
                  {disruption.riskType || disruption.Risk_Type}
                </div>
              </TableCell>
              <TableCell
                title={disruption.Incident_Headline || "-"}
                sx={{
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                className={style.tableCell}
                data-tooltip-content={disruption.Incident_Headline || "-"}
              >
                {disruption.Incident_Headline || "-"}
              </TableCell>
              <TableCell
               className={style.tableCell}
              >
                {typeof disruption.City !== "undefined"
                  ? disruption.City
                  : disruption.location || "-"}
              </TableCell>
              <TableCell
                className={style.tableCell}
              >
                {disruption.State || "-"}
              </TableCell>
              <TableCell
                className={style.tableCell}
              >
                {disruption.Country || "-"}
              </TableCell>
              <TableCell
                className={style.tableCell}
              >
                {disruption?.Affected_Facility_Count || "-"}
              </TableCell>
              <TableCell
                className={style.tableCell}
              >
                <span
                  style={{
                    color:
                      disruption.severity === "SEVERE" ||
                      disruption.Severity === "SEVERE"
                        ? "#FF544F"
                        : disruption.severity === "MODERATE" ||
                          disruption.Severity === "MODERATE"
                        ? "#FF9E00"
                        : "#FFE300",
                    fontWeight: "bold",
                    backgroundColor: "#00000060",
                    padding: "4px 8px",
                    borderRadius: "8px",
                  }}
                >
                  {disruption.severity || disruption.Severity}
                </span>
              </TableCell>
              <TableCell
                sx={{
                  textAlign: "center",
                }}
                className={style.tableCell}
              >
                <Badge
                  badgeContent={disruption?.Incid_Score || "-"}
                  color="primary"
                  sx={{
                    ".MuiBadge-badge": {
                      backgroundColor: "#3b82f6",
                      color: "#fff",
                    },
                  }}
                />
              </TableCell>
              <TableCell
                className={style.tableCellWrap}
              >
                {disruption.date || disruption.Start_Date}
              </TableCell>
              <TableCell
                className={style.tableCellWrap}
              >
                {disruption?.Expected_End_Date || "-"}
              </TableCell>
              <TableCell
                className={style.tableCellWrap}
              >
                {disruption.Created_Date || "-"}
              </TableCell>
              <TableCell
                className={style.tableCellWrap}
              >
                {disruption.Lane || "-"}
              </TableCell>
              <TableCell
                className={style.tableCellWrap}
              >
                {disruption.Out_Mat || "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #1a2a51",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={600} color="white">
            External Disruption
          </Typography>
          <Tooltip
            title={
              <div style={{
                color: "black",
                fontWeight: 400
              }}>
                Events outside the supply chain that impact product availability or movement
              </div>
            }
            arrow
          >
            <IconButton size="small" sx={{ color: "#ccc" }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" gap={1}>
          <ChartDialog
            title="External Disruption"
            trigger={
              <IconButton size="small" sx={{ color: "#ccc" }}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            }
            isTableView
          >
            <Box maxWidth="80vw" overflow="auto">
              <DisruptionTable />
            </Box>
          </ChartDialog>
          <IconButton size="small" sx={{ color: "#ccc" }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box flex={1} overflow="auto">
        <DisruptionTable />
      </Box>
    </Box>
  );
}
